import { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Box,
  ButtonBase,
  Collapse,
  Tooltip,
  Typography,
} from '@mui/material';
import { HexColorPicker } from 'react-colorful';
import { formatTranslation } from 'intl';
import { RichTextTranslationKeys } from 'intl/keys';
import { isNonEmptyArray } from 'shared/utils/array';
import { BASIC_TEXT_COLORS } from './palette';
import {
  isCompleteHexColor,
  recentCustomTextColors,
  toPickerColor,
} from './model';
import { TextColorSwatches } from './TextColorSwatches';
import {
  customPickerSx,
  HexInput,
  menuSectionTitleSx,
  spectrumSwatchSx,
} from './styles';

const BASIC_COLOR_VALUES = BASIC_TEXT_COLORS.map(({ color }) => color);

interface TextColorMenuProps {
  selectionColor: string | undefined;
  /** Colours the user used, newest first. */
  usedColors: readonly string[];
  /** A swatch: applied, remembered, the menu closes. */
  onPick: (color: string) => void;
  /** A custom colour while it is being chosen (dragged or typed): applied; remembered when the menu closes. */
  onCustomColor: (color: string) => void;
}

/**
 * The colour menu of word processors (PT-7250): the basic colours with a
 * spectrum swatch that opens a picker for any other colour, and the recent
 * ones. Removing the colour is a button of the toolbar.
 */
export const TextColorMenu: FC<TextColorMenuProps> = ({
  selectionColor,
  usedColors,
  onPick,
  onCustomColor,
}) => {
  const intl = useIntl();
  const [isPickerOpen, setPickerOpen] = useState(false);
  const moreColorsLabel = formatTranslation(
    intl,
    RichTextTranslationKeys.richtextTextColorMore
  );
  const recentColors = recentCustomTextColors(
    usedColors,
    BASIC_COLOR_VALUES
  );

  const basicSwatches = BASIC_TEXT_COLORS.map(({ color, nameKey }) => ({
    color,
    label: formatTranslation(intl, nameKey),
  }));
  const recentSwatches = recentColors.map((color) => ({
    color,
    label: color,
  }));

  /** `#E53` is a valid colour too: applying it while `#E53935` is typed would overwrite the field. */
  const applyTypedColor = (value: string): void => {
    if (isCompleteHexColor(value)) {
      onCustomColor(value);
    }
  };

  return (
    <>
      <Typography variant='caption' sx={menuSectionTitleSx}>
        {formatTranslation(
          intl,
          RichTextTranslationKeys.richtextTextColorBasic
        )}
      </Typography>
      <TextColorSwatches
        swatches={basicSwatches}
        selectedColor={selectionColor}
        onPick={onPick}
        dataQa='rich-text-basic-colors'
      >
        <Tooltip title={moreColorsLabel}>
          <ButtonBase
            onClick={() => setPickerOpen(!isPickerOpen)}
            aria-label={moreColorsLabel}
            aria-expanded={isPickerOpen}
            sx={spectrumSwatchSx}
            data-qa='rich-text-more-colors'
          />
        </Tooltip>
      </TextColorSwatches>
      <Collapse in={isPickerOpen} unmountOnExit>
        <Box sx={customPickerSx}>
          <HexColorPicker
            color={toPickerColor(selectionColor)}
            onChange={onCustomColor}
          />
          <HexInput
            color={toPickerColor(selectionColor)}
            onChange={applyTypedColor}
            prefixed
            aria-label={moreColorsLabel}
          />
        </Box>
      </Collapse>
      {isNonEmptyArray(recentSwatches) && (
        <>
          <Typography variant='caption' sx={menuSectionTitleSx}>
            {formatTranslation(
              intl,
              RichTextTranslationKeys.richtextTextColorRecent
            )}
          </Typography>
          <TextColorSwatches
            swatches={recentSwatches}
            selectedColor={selectionColor}
            onPick={onPick}
            dataQa='rich-text-recent-colors'
          />
        </>
      )}
    </>
  );
};
